import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import { showStats } from 'actions/gameActions';
import { stopAudio as stopSharedAudio, toggleAudio, playCorrectSound, playIncorrectSound, playWatch } from 'actions/audioActions';
import { CHECK_ANSWER, CORRECT_ANSWER, GO_BACK, HELP, NEXT, SPEAK } from 'consts/buttons';
import { ActivityButtons, Instructions, ProblemsProgress, WhiteBox } from 'components/flink-play';
import { activitiesURL, imagesURL } from 'config';

import { MODE, facesForMode, buildSessionOrder } from './flashCardsHelpers';
import { isExactMatch, isBlankSubmission } from './exactMatch';
import { shouldAutoplayInstruction } from './instructionAudio';
import {
  FRAME_LIST_URL,
  FRAME_FALLBACK_URL,
  parseFrameKeys,
  pickFrameName,
  frameUrlFor,
} from './frameDesign';
import classes from './FlashCards.module.scss';

const DEFAULT_DELAY = 3; // seconds (FC-09)
const DEFAULT_SHOW_ANSWER = '2'; // incorrect attempts before reveal available (FC-10, D4)

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class FlashCards extends Component {
  state = {
    started: false,
    order: [],
    index: 0,
    face: 0,
    viewedFaces: {},
    input: '',
    attempts: 0,
    firstAttemptCorrect: {},
    completedCards: {},
    revealed: false,
    picked: null,
    feedback: null,
    done: false,
    // FC-010: the randomly-chosen frame design URL for this open. null until
    // discovery resolves; frameUrl() uses the known fallback meanwhile and on error.
    chosenFrameUrl: null,
    // ITEM-3 (FC-011): measured reserved top (px) for the instruction band so the
    // card never overlaps a tall (3-4 line / long-locale) instruction. null until
    // the band is measured; the CSS falls back to the standing default meanwhile.
    instrReservePx: null,
  };

  delayTimer = null;
  completionLatched = false;
  audioRef = React.createRef(); // defect 10: controllable face-audio element
  // ITEM-2 (FC-011): id of the card whose INSTRUCTION audio we have already
  // auto-played this session. One autoplay per active card — never re-fired by a
  // flip, a re-render or typing (those keep the same active card id). Reset on
  // (re)start and unmount. null = no card auto-played yet.
  lastAutoplayedInstructionCardId = null;
  // ITEM-3 (FC-011): ref to the template-local wrapper around the shared Instructions
  // band, used to measure the band's ACTUAL rendered height (it wraps to more lines
  // for long text / locales) and reserve exactly that height + a gap above the card.
  instrBandRef = React.createRef();
  stageRef = React.createRef();
  _instrResizeObserver = null;
  // The DEFAULT reserved top (px) used before measurement and when no instruction is
  // present — matches the prior accepted single/2-line reserve (140px). Kept as the
  // floor so 1/2-line cases never regress (reserve is never smaller than this).
  INSTR_RESERVE_DEFAULT_PX = 140;
  INSTR_GAP_PX = 18;

  componentDidMount() {
    document.addEventListener('startGame', this.startGame);
    // FC-010: choose the frame design ONCE per activity open. Stable thereafter.
    this.selectFrameDesign();
    // ITEM-3: start observing the instruction band so the reserved top tracks its
    // real height (if an instruction is already present on first render).
    this.observeInstructionBand();
  }

  componentDidUpdate(prevProps, prevState) {
    // ITEM-3: when the active card changes (or the game just started), the instruction
    // band may have a different height — re-attach the observer to the new band node
    // and re-measure. Keying on index/started keeps this cheap (no measure per render).
    if (
      prevState.index !== this.state.index ||
      prevState.started !== this.state.started
    ) {
      this.teardownInstructionObserver();
      this.observeInstructionBand();
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    this.lastAutoplayedInstructionCardId = null; // ITEM-2: reset autoplay guard
    this.teardownInstructionObserver(); // ITEM-3
    this.clearDelay();
    this.stopAudio();
    document.removeEventListener('startGame', this.startGame);
  }

  clearDelay = () => {
    if (this.delayTimer) {
      clearTimeout(this.delayTimer);
      this.delayTimer = null;
    }
  };

  // defect 10: stop/rewind the native face audio on card change / exit / unmount.
  // Instruction audio started via SPEAK/Instructions plays through the SHARED
  // audio player (window.audioPlayer via toggleAudio), so pausing the native
  // <audio> element is not enough — also stop the shared player through the
  // existing public stopAudio action. Shared audio logic is unchanged.
  stopAudio = () => {
    const el = this.audioRef.current;
    if (el && typeof el.pause === 'function') {
      try {
        el.pause();
        el.currentTime = 0;
      } catch (e) {
        /* no-op */
      }
    }
    // Instruction audio started via SPEAK/Instructions plays through the SHARED
    // audio player (window.audioPlayer via toggleAudio). stopSharedAudio is the
    // existing public IMPERATIVE action (returns undefined, calls audioPlayer.stop
    // directly) — call it directly, NOT via dispatch/connect, which would
    // dispatch(undefined). Shared audio logic is unchanged.
    if (typeof stopSharedAudio === 'function') {
      stopSharedAudio();
    }
  };

  // R14: play (toggle) the face audio through the EXISTING shared player
  // (toggleAudio → window.audioPlayer.togglePlaySrc), the same mechanism
  // PlayAudioButton used — but driven by a template-local, properly-NAMED <button>
  // so the control has an accessible name (the shared PlayAudioButton renders
  // alt="" with no aria-label and forwards no aria props; we do not modify it).
  // Activation stops propagation so the card does NOT flip; stopPropagation on the
  // wrapper's keydown keeps Enter/Space on the button from reaching the card's flip
  // handler, while the card itself (outside the button) still flips on Enter/Space.
  playFaceAudio = (src) => (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (src) toggleAudio(src);
  };

  // ITEM-2 (FC-011): auto-play the ACTIVE card's INSTRUCTION audio once, when that
  // card becomes active AFTER a normal game start. This is DISTINCT from the R14
  // on-card FACE audio (playFaceAudio): instruction audio is the optional per-card
  // instruction (FC-08), played through the SAME shared mechanism the global SPEAK
  // button uses — the public `playInstructionsAudio` DOM event that the shared
  // `Instructions` block listens for (see Instructions.playInstructionsAudio ->
  // toggleAudio). We reuse that exact seam rather than resolving the URL ourselves,
  // so locale asset resolution stays identical to SPEAK/Instructions.
  //
  // Browser autoplay policy: this only ever runs synchronously inside a user-gesture
  // call stack — startGame (dispatched by the Start click) for the first card, and
  // goNext (a Next click or a post-answer scheduled advance that itself followed a
  // click) for subsequent cards. We never autoplay on load without that gesture, so
  // we do not bypass the policy; if a browser still blocks it, the user replays with
  // the existing SPEAK button.
  //
  // One autoplay per active card: guarded by lastAutoplayedInstructionCardId, so a
  // flip, a re-render, or typing (all of which keep the same active card) never
  // retriggers it. Called AFTER the active-card state is committed (setState
  // callback) so the child Instructions has set its current instruction and its
  // event listener is mounted.
  autoplayInstructionAudio = () => {
    if (this._unmounted) return;
    const card = this.currentCard();
    if (!shouldAutoplayInstruction(card, this.lastAutoplayedInstructionCardId)) return;
    // Mark BEFORE the async wait so a flip/re-render during the wait cannot queue a
    // second autoplay for the same card.
    this.lastAutoplayedInstructionCardId = card.id;
    const cardId = card.id;
    // RACE FIX: the shared Instructions block resolves its current instruction in its
    // OWN async setState (componentDidMount/componentDidUpdate -> setCurrentInstruction),
    // and `playInstructionsAudio` is a no-op until that lands (it early-returns while
    // `this.state.instruction` is empty). Dispatching the event synchronously in our
    // setState callback fired BEFORE the instruction resolved → no sound. So wait for
    // the instruction band to actually be in the DOM (our instrBandRef gains a child
    // once InstructionsTextBlock has text), then dispatch ONCE. Bounded rAF retries
    // avoid assuming a fixed delay and never spin forever.
    const MAX_FRAMES = 30; // ~0.5s at 60fps — ample for the async setState to flush
    const bandReady = () => {
      const wrap = this.instrBandRef.current;
      return !!(wrap && wrap.querySelector('*'));
    };
    const fire = () => {
      if (typeof document !== 'undefined' && typeof document.dispatchEvent === 'function') {
        document.dispatchEvent(new Event('playInstructionsAudio'));
      }
    };
    let frames = 0;
    const tick = () => {
      // Abort if we unmounted or the active card changed while waiting (stale autoplay).
      if (this._unmounted) return;
      const current = this.currentCard();
      if (!current || current.id !== cardId) return;
      if (bandReady()) { fire(); return; }
      if (frames++ >= MAX_FRAMES) { fire(); return; } // last-resort: try anyway
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(tick);
      } else {
        fire();
      }
    };
    tick();
  };

  // ITEM-3 (FC-011): measure the ACTUAL rendered instruction band and reserve its
  // real height + a gap above the card, so a 3-4 line (or long-locale) instruction
  // never overlaps the card — WITHOUT hard-coding another magic pixel value.
  //
  // The shared InstructionsTextBlock band is position:absolute (top:2% of the canvas)
  // and wraps to a variable number of lines. We wrap <Instructions> in a template-
  // local div (instrBandRef); the band is its DOM descendant even though positioned
  // absolutely, so we read the band node's real painted rect. Reserve =
  // (band bottom relative to the stage top) + gap, floored at the prior accepted
  // single/2-line reserve so 1/2-line cases never regress. The card's reserved box
  // (.content top padding) is driven by this via the --flc-instr-reserve variable.
  //
  // .contentNoInstr uses the SAME standing reserve (no override), so switching
  // between a card with and without an instruction does not resize the card for a
  // given reserve; a taller instruction legitimately reserves more (card as large as
  // the remaining canvas allows), which is the stated requirement.
  measureInstructionBand = () => {
    if (this._unmounted) return;
    const wrap = this.instrBandRef.current;
    const stage = this.stageRef.current;
    if (!wrap || !stage) return;
    // The band is the absolutely-positioned descendant; measure its real rect.
    const bandEl = wrap.querySelector('*') || wrap;
    let reserve = this.INSTR_RESERVE_DEFAULT_PX;
    try {
      const bandRect = bandEl.getBoundingClientRect();
      const stageRect = stage.getBoundingClientRect();
      if (bandRect && bandRect.height > 0) {
        const bottomWithinStage = bandRect.bottom - stageRect.top;
        reserve = Math.max(this.INSTR_RESERVE_DEFAULT_PX, Math.round(bottomWithinStage + this.INSTR_GAP_PX));
      }
    } catch (e) {
      /* measurement failed — keep the standing default, never overlap by under-reserving */
    }
    if (reserve !== this.state.instrReservePx) {
      this.setState({ instrReservePx: reserve });
    }
  };

  // Observe band size changes (font load, text/locale change, viewport resize) so the
  // reserve stays correct. ResizeObserver covers reflow; window resize covers vmin.
  observeInstructionBand = () => {
    if (typeof window === 'undefined') return;
    // measure after layout settles
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(this.measureInstructionBand);
    } else {
      this.measureInstructionBand();
    }
    const wrap = this.instrBandRef.current;
    if (wrap && typeof window.ResizeObserver === 'function' && !this._instrResizeObserver) {
      this._instrResizeObserver = new window.ResizeObserver(() => this.measureInstructionBand());
      // observe the band descendant if present, else the wrapper
      this._instrResizeObserver.observe(wrap.querySelector('*') || wrap);
    }
    if (!this._instrWindowResize) {
      this._instrWindowResize = () => this.measureInstructionBand();
      window.addEventListener('resize', this._instrWindowResize);
    }
  };

  teardownInstructionObserver = () => {
    if (this._instrResizeObserver) {
      try { this._instrResizeObserver.disconnect(); } catch (e) { /* no-op */ }
      this._instrResizeObserver = null;
    }
    if (this._instrWindowResize && typeof window !== 'undefined') {
      window.removeEventListener('resize', this._instrWindowResize);
      this._instrWindowResize = null;
    }
  };

  // FC-010: the decorative frame now supports MULTIPLE designs, one randomly chosen
  // per activity open (mount) and held STABLE for the whole activity lifetime
  // (across renders, flips, next-cards and answers). A fresh open may choose again;
  // randomness may repeat — we do NOT promise a different design each time.
  //
  // Designs live under the human-authoritative public prefix in bucket flink-2.0-fs:
  //   https://s3.us-east-2.amazonaws.com/flink-2.0-fs/Images/TemplateGraphics/FlashCards/
  // as numbered PNGs (flashcard1.png, flashcard2.png, ... — lowercase, exact keys).
  // These are EXPLICIT template-local constants (in ./frameDesign) — NOT derived from
  // `imagesURL` (REACT_APP_FILE_BUCKET), which may resolve to a different bucket/CDN.
  //
  // Discovery WITHOUT a hardcoded count and WITHOUT a backend/catalog change: the
  // bucket already permits anonymous ListBucket with CORS (Access-Control-Allow-
  // Origin: *), verified read-only. We fetch the public list-objects-v2 XML, keep
  // only DIRECT `flashcard<n>.png` children under this prefix (excluding nested or
  // unrelated keys), and pick one at random. This requires no ListBucket/CORS/policy
  // change by us and no S3 writes. If listing fails or returns nothing, we fall back
  // to the single known frame (resources/FlashCard.png, served from FlinkPlay), so the
  // activity is never blank. A chosen image that fails to load also falls back.
  //
  // The frame URL used for painting. Returns the chosen design once discovery has
  // resolved; until then (and on any failure) the known fallback. Stable per open
  // because `chosenFrameUrl` is set exactly once in state.
  frameUrl = () => this.state.chosenFrameUrl || FRAME_FALLBACK_URL;

  // Discover the available designs at mount and choose ONE at random, once.
  // No-op (keeps the fallback) on any error or empty result. Guarded against
  // setState-after-unmount.
  //
  // IMPORTANT (FC-010 follow-up): we PRELOAD the chosen design and only publish
  // `chosenFrameUrl` AFTER it has loaded successfully. The frame paints via a CSS
  // `::before` background-image bound to `--flc-frame-url`; publishing the URL before
  // the PNG is in the browser cache would switch the CSS off the known fallback
  // immediately and leave a transient BLANK frame until the new image arrives. By
  // preloading first, the known frame stays painted while pending, and we swap only
  // when the new art is ready — no blank flash. On preload error we never publish, so
  // the known frame remains. Selection still happens exactly once (choose-once).
  selectFrameDesign = () => {
    if (typeof fetch !== 'function') return; // non-browser/test harness: keep fallback
    fetch(FRAME_LIST_URL, { method: 'GET' })
      .then((res) => (res && res.ok ? res.text() : Promise.reject(new Error('list failed'))))
      .then((xml) => {
        const names = parseFrameKeys(xml);
        const pick = pickFrameName(names);
        if (!pick) return; // nothing usable — keep the known fallback
        this.preloadThenPublish(frameUrlFor(pick));
      })
      .catch(() => {
        /* listing/parse failed — keep the known fallback frame, never blank */
      });
  };

  // Preload `url`; publish it to state only on successful load. Keeps the known frame
  // painted while pending and on error (never blank). Guarded against unmount.
  preloadThenPublish = (url) => {
    if (typeof Image !== 'function') {
      // No Image constructor (test harness): publish directly — the DOM <img> probe
      // still guards a real load failure at paint time.
      if (!this._unmounted) this.setState({ chosenFrameUrl: url });
      return;
    }
    const pre = new Image();
    pre.onload = () => {
      if (!this._unmounted) this.setState({ chosenFrameUrl: url });
    };
    pre.onerror = () => {
      /* chosen design failed to preload — keep the known fallback, never blank */
    };
    pre.src = url;
  };

  // If the chosen design fails to load in the browser, revert to the known frame so
  // the card never renders without a frame. Only steps DOWN to the fallback (never
  // re-randomizes), preserving stability.
  handleFrameError = () => {
    if (!this._unmounted && this.state.chosenFrameUrl !== FRAME_FALLBACK_URL) {
      this.setState({ chosenFrameUrl: FRAME_FALLBACK_URL });
    }
  };

  // ---- locale-aware activity upload base (defect 4) ----
  getLocaleInfo = () => {
    const { activity, solutionLocale } = this.props;
    const monolingual = !!(activity && activity.activity && activity.activity.monolingual);
    const code = monolingual ? '' : (solutionLocale && solutionLocale.code) || '';
    const versionType = !code || code === 'en' ? '' : `immersion_${code}`;
    return { code, versionType };
  };

  // The EFFECTIVE version type. The TemplateWrapper is the authority: it resolves
  // the version via its priority chain (explicit / share-preview / editor / runtime
  // locale, each through getVersionTypeFromLocale) AND applies the base fallback,
  // then passes the result down as the `versionType` prop. PREFER that prop so a
  // non-English base + English translation, an explicit versionType, and any
  // non-`immersion` version all resolve exactly as the rest of Play does. Fall back
  // to the local locale derivation ONLY when the prop is absent (e.g. a test
  // harness mounts the component without the wrapper). Either way, re-confirm the
  // version's data is actually present so content+media never split: content falls
  // to base in lockstep with media.
  effectiveVersionType = () => {
    const data = (this.props.activity && this.props.activity.data) || {};
    const hasVersionData = (vt) => {
      if (!vt) return false;
      const lv = data.languageVersionData && data.languageVersionData[vt];
      return !!(lv && Array.isArray(lv.problems));
    };
    const fromProp = this.props.versionType;
    // The prop is AUTHORITATIVE whenever the wrapper passed it — INCLUDING '' which
    // is the wrapper's explicit BASE decision (line 464: hasLocalizedGameData ?
    // requestedVersionType : ''). So check PRESENCE (typeof string), not truthiness:
    // treating '' as "absent" would wrongly fall through to the local derivation and
    // could resolve an immersion version the wrapper deliberately rejected.
    if (typeof fromProp === 'string') {
      return hasVersionData(fromProp) ? fromProp : '';
    }
    // Fallback ONLY when the prop is absent/undefined (e.g. a test harness mounts
    // the component without the wrapper): derive from the solution locale as before.
    const { versionType } = this.getLocaleInfo();
    return hasVersionData(versionType) ? versionType : '';
  };

  activityId = () =>
    (this.props.activity && this.props.activity.activity && this.props.activity.activity._id) || '';

  // Authored media live in the activity uploads folder (+ locale path), NOT under /Images.
  // PREFER the wrapper-resolved `contentFolder` prop (it already encodes the exact
  // languageVersionData/<vt>/ vs gamedata/ choice for the resolved version); fall
  // back to reconstructing it from the effective version type when the prop is
  // absent (harness). This keeps media on the SAME version as getGameData's content.
  assetBaseUrl = () => {
    const versionType = this.effectiveVersionType();
    const id = this.activityId();
    // When a localized version IS in effect, prefer the wrapper's resolved
    // contentFolder prop (it encodes the exact languageVersionData/<vt>/ path);
    // but only when effectiveVersionType agrees a version is active, so media can
    // never point at a localized folder while getGameData served base content.
    if (versionType) {
      const fromProp = this.props.contentFolder;
      if (typeof fromProp === 'string' && fromProp) {
        return fromProp.endsWith('/') ? fromProp : `${fromProp}/`;
      }
      return `${activitiesURL}/${id}/languageVersionData/${versionType}/`;
    }
    return `${activitiesURL}/${id}/gamedata/`;
  };

  assetUrl = (filename) => (filename ? `${this.assetBaseUrl()}${filename}` : '');

  getGameData = () => {
    const { activity } = this.props;
    const data = activity && activity.data ? activity.data : {};
    const versionType = this.effectiveVersionType();
    const gd = versionType
      ? (data.languageVersionData && data.languageVersionData[versionType])
      : data.gameData;
    return gd || {};
  };

  getOptions = () => (this.props.activity && this.props.activity.data && this.props.activity.data.options) || {};

  // Route labels through the existing translate public API (status.translate, same
  // source as withTranslate). `id=0` + an English fallback is the project convention
  // for a not-yet-stringized label. Harness-safe: when the prop is absent (unit
  // harness), return the fallback string unchanged so nothing throws.
  t = (id, fallback) =>
    typeof this.props.translate === 'function' ? this.props.translate(id, fallback) : fallback;

  // defect 8: expose authored font options for local rendering.
  fontStyle = () => {
    const o = this.getOptions();
    const style = {};
    if (o.fontFamily) style.fontFamily = o.fontFamily;
    if (o.fontSize) style.fontSize = `${parseInt(o.fontSize, 10)}px`;
    if (o.fontColor) style.color = o.fontColor;
    return style;
  };

  startGame = () => {
    const gameData = this.getGameData();
    const options = this.getOptions();
    const mode = gameData.mode || MODE.MEMORY;
    const problems = Array.isArray(gameData.problems) ? gameData.problems : [];
    const order = buildSessionOrder(problems, !!options.randomOrder);

    this.completionLatched = false;
    this.lastAutoplayedInstructionCardId = null; // ITEM-2: fresh session, allow autoplay
    this.clearDelay();
    this.stopAudio();
    this.setState({
      started: true,
      mode,
      order,
      index: 0,
      face: 0,
      viewedFaces: order.length ? { [order[0].id]: { 0: true } } : {},
      input: '',
      attempts: 0,
      firstAttemptCorrect: {},
      completedCards: {},
      revealed: false,
      picked: null,
      feedback: null,
      done: false,
    }, this.autoplayInstructionAudio); // ITEM-2: autoplay first card's instruction audio (follows the Start gesture)
  };

  currentCard = () => this.state.order[this.state.index] || null;

  // null => reveal disabled (n/a). Otherwise the incorrect-attempt threshold.
  showAnswerThreshold = () => {
    const raw = this.getOptions().showAnswer;
    const val = raw === undefined || raw === null ? DEFAULT_SHOW_ANSWER : raw;
    if (val === 'n/a') return null;
    const n = parseInt(val, 10);
    return Number.isFinite(n) ? n : parseInt(DEFAULT_SHOW_ANSWER, 10);
  };

  // reveal is AVAILABLE only once attempts reached the threshold (manual, D4).
  revealAvailable = () => {
    const t = this.showAnswerThreshold();
    return t !== null && this.state.attempts >= t;
  };

  delaySeconds = () => {
    const d = this.getOptions().delayBeforeNext;
    const n = parseInt(d, 10);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_DELAY;
  };

  // ---- flip ----
  flip = () => {
    if (this.state.done) return;
    const nextFace = this.state.face === 0 ? 1 : 0;
    const card = this.currentCard();
    this.stopAudio(); // defect 10: stop audio when the face changes
    this.setState((s) => {
      const viewed = { ...(s.viewedFaces[card.id] || {}), [nextFace]: true };
      return { face: nextFace, viewedFaces: { ...s.viewedFaces, [card.id]: viewed } };
    });
  };

  stop = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
  };

  bothFacesViewed = (card) => {
    const v = this.state.viewedFaces[card.id] || {};
    return !!v[0] && !!v[1];
  };

  markCompleted = (card, wasFirstAttemptCorrect) => {
    this.setState((s) => ({
      completedCards: { ...s.completedCards, [card.id]: true },
      firstAttemptCorrect:
        wasFirstAttemptCorrect === undefined
          ? s.firstAttemptCorrect
          : {
              ...s.firstAttemptCorrect,
              [card.id]:
                s.firstAttemptCorrect[card.id] === undefined
                  ? !!wasFirstAttemptCorrect
                  : s.firstAttemptCorrect[card.id],
            },
    }));
  };

  scheduleAdvance = () => {
    this.clearDelay();
    this.delayTimer = setTimeout(this.goNext, this.delaySeconds() * 1000);
  };

  goNext = () => {
    this.clearDelay();
    this.stopAudio();
    const { index, order } = this.state;
    if (index + 1 >= order.length) {
      this.finish();
      return;
    }
    const nextCard = order[index + 1];
    this.setState((s) => ({
      index: s.index + 1,
      face: 0,
      input: '',
      attempts: 0,
      revealed: false,
      picked: null,
      feedback: null,
      viewedFaces: { ...s.viewedFaces, [nextCard.id]: { 0: true } },
    }), this.autoplayInstructionAudio); // ITEM-2: autoplay the newly-active card's instruction audio
  };

  finish = () => {
    if (this.completionLatched) return;
    this.completionLatched = true;
    this.clearDelay();
    this.stopAudio();

    const { order, mode } = this.state;
    const total = order.length;

    if (mode === MODE.MEMORY) {
      // Self-study: non-scored. withScore:false keeps the reducer from scoring it.
      // ActivityResults.initStats reads `predefinedMastered` (NOT `mastered`) to
      // dispatch mastery through the existing progress actions — use that flag.
      this.props.showStats({
        withScore: false,
        predefinedMastered: true,
        data: { allProblemsCount: total },
      });
    } else {
      const correct = order.filter((c) => this.state.firstAttemptCorrect[c.id]).length;
      this.props.showStats({
        withScore: true,
        data: {
          allProblemsCount: total,
          problemsAnsweredCorrectly: correct,
          problemsAnsweredIncorrectly: total - correct,
        },
      });
    }

    this.setState({ done: true });
  };

  // ---- Memory ----
  memoryNext = () => {
    const card = this.currentCard();
    if (!this.bothFacesViewed(card)) return;
    this.markCompleted(card);
    this.goNext();
  };

  // ---- Fill-in ----
  onInput = (e) => this.setState({ input: e.target.value }); // raw, never trimmed

  checkFillIn = () => {
    // Guard: the card is settled once answered correctly OR revealed (reveal is a
    // terminal outcome that already scheduled the advance). A Check click during
    // the post-reveal delay must NOT replay feedback audio or bump attempts.
    if (this.state.done || this.state.feedback === 'correct' || this.state.revealed) return;
    const card = this.currentCard();
    const raw = this.state.input;
    if (isBlankSubmission(raw)) return; // D3: blank consumes no attempt

    // R11: case sensitivity is the ONLY configurable difference (default off =
    // case-insensitive). No trim/normalize. Whitespace/punctuation/diacritics
    // preserved by isExactMatch.
    const correct = isExactMatch(raw, card.expectedAnswer, !!this.getOptions().caseSensitive);
    const isFirst = this.state.attempts === 0;

    if (correct) {
      this.markCompleted(card, isFirst);
      this.setState({ feedback: 'correct' });
      // R11: established feedback AUDIO (as other templates), not custom text.
      this.props.playCorrectSound();
      this.scheduleAdvance();
      return;
    }

    // defect 9: wrong submit does NOT auto-reveal; it only increments attempts and
    // records first-attempt incorrect once. Reveal stays manual (Correct Answer).
    this.props.playIncorrectSound(); // R11: established wrong-answer feedback audio
    this.setState((s) => ({
      attempts: s.attempts + 1,
      feedback: 'incorrect',
      firstAttemptCorrect: {
        ...s.firstAttemptCorrect,
        [card.id]: s.firstAttemptCorrect[card.id] === undefined ? false : s.firstAttemptCorrect[card.id],
      },
    }));
  };

  // defect 9: manual reveal; obeys n/a; never grants first-attempt credit; and
  // after reveal it completes the card and schedules exactly one delayed advance.
  showCorrectAnswer = () => {
    if (this.state.done) return;
    if (this.state.revealed) return; // idempotent: a repeated reveal queues nothing more
    if (this.showAnswerThreshold() === null) return; // n/a disables reveal
    if (!this.revealAvailable()) return; // not before threshold
    const card = this.currentCard();
    // ensure first-attempt is recorded as NOT correct (reveal cannot grant credit)
    this.markCompleted(card, false);
    this.setState({ revealed: true, feedback: 'incorrect' });
    // R11/point 11: Show Answer uses the SAME established reveal feedback audio as
    // every other template's showAnswer() — the shared `playWatch` action. Not a
    // custom sound, not playIncorrectSound (reveal is a distinct outcome).
    this.props.playWatch();
    this.scheduleAdvance();
  };

  // ---- Pick One: R10 (2026-10-24, OVERRIDES the legacy on-card Check Answer):
  // the GLOBAL Bar Check Answer button checks the currently VISIBLE face
  // atomically — no "choose this face" step and no `picked` state, so there is no
  // stale-setState race (we read the visible face directly here). Feedback uses
  // the established shared AUDIO, not custom text. ----
  checkPickOne = () => {
    // Same terminal-state guard as checkFillIn: once revealed, a Check click during
    // the post-reveal delay must be a no-op (no sound replay, no attempt bump).
    if (this.state.done || this.state.feedback === 'correct' || this.state.revealed) return;
    const card = this.currentCard();
    const chosen = this.state.face; // the currently visible face IS the selection
    const isFirst = this.state.attempts === 0;
    const correct = chosen === card.correctSideIndex;

    if (correct) {
      this.markCompleted(card, isFirst);
      this.setState({ feedback: 'correct' });
      this.props.playCorrectSound(); // R10: established correct-answer feedback audio
      this.scheduleAdvance();
      return;
    }

    // FC-012 (defect 2): a wrong Check Answer NEVER auto-reveals. It only records
    // the incorrect attempt (and first-attempt-incorrect once) and plays the
    // established wrong-answer feedback. Once attempts reach the threshold, the
    // GLOBAL Correct Answer button appears (modeButtons via revealAvailable), and
    // the actual reveal/complete/reveal-sound/advance happen ONLY on an explicit
    // click of that button (showCorrectAnswer) — exactly like Fill-in. No credit
    // is ever granted after a wrong attempt. (Removed the former auto-reveal side
    // effects: markCompleted / setState(revealed) / playWatch / scheduleAdvance.)
    this.props.playIncorrectSound(); // R10: established wrong-answer feedback audio
    this.setState((s) => ({
      attempts: s.attempts + 1,
      feedback: 'incorrect',
      firstAttemptCorrect: {
        ...s.firstAttemptCorrect,
        [card.id]: s.firstAttemptCorrect[card.id] === undefined ? false : s.firstAttemptCorrect[card.id],
      },
    }));
  };

  // R09: display images as large as the face allows while PRESERVING aspect ratio
  // (object-fit:contain, no crop/stretch) and capping upscale at 200% of the
  // image's INTRINSIC size. The face box bounds the upper axis; the per-image
  // 2x cap (set from naturalWidth/Height on load) bounds the other, so a small
  // clipart never balloons past 200% and a large image still fits the face.
  onFaceImageLoad = (e) => {
    const img = e && e.target;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    img.style.maxWidth = `${img.naturalWidth * 2}px`;
    img.style.maxHeight = `${img.naturalHeight * 2}px`;
  };

  renderFace = (item, faceIdx) => {
    if (!item) return null;
    if (item.image) {
      return (
        <img
          className={classes.faceImage}
          src={this.assetUrl(item.image)}
          alt={`${this.t(0, 'face')} ${faceIdx + 1}`}
          onLoad={this.onFaceImageLoad}
        />
      );
    }
    if (item.audio) {
      // R14: an ON-CARD Play control, NOT native <audio controls> chrome. It reuses
      // the EXISTING shared player via toggleAudio(url) and the SAME project speaker
      // asset (Images/Audio/audio_play.png) as PlayAudioButton — but as a
      // template-local, properly-NAMED <button> so it has an accessible name (the
      // shared PlayAudioButton has none and we must not modify it). Click AND
      // keyboard (Enter/Space fire a native button's onClick) play without flipping:
      // the handler stops propagation and the wrapper swallows the keydown so it
      // never reaches the card's flip handler. The card itself (outside the button)
      // still flips on Enter/Space. A hidden native <audio> keeps the stopAudio()
      // element-pause belt-and-suspenders; the real stop path is the shared
      // stopSharedAudio() already driven on flip/next/restart/unmount.
      const audioSrc = this.assetUrl(item.audio);
      return (
        <div className={classes.audioWrap} onClick={this.stop} onKeyDown={this.stop}>
          <button
            type="button"
            className={classes.audioPlayBtn}
            onClick={this.playFaceAudio(audioSrc)}
            aria-label={this.t(0, 'Play audio')}
            title={this.t(0, 'Play audio')}
          >
            <img src={`${imagesURL}/Images/Audio/audio_play.png`} alt="" aria-hidden="true" />
          </button>
          <audio ref={this.audioRef} className={classes.hiddenAudio} src={audioSrc} preload="none" />
        </div>
      );
    }
    return <span className={classes.faceText} style={this.fontStyle()}>{item.text}</span>;
  };

  renderCardFace = (card) => {
    const { mode, face } = this.state;
    const faces = facesForMode(mode);

    if (mode === MODE.FILL_IN) {
      if (face === 0) return this.renderFace(card.items[0], 0);
      return (
        <div className={classes.inputWrap} onClick={this.stop}>
          <input
            className={classes.answerInput}
            style={this.fontStyle()}
            type="text"
            value={this.state.input}
            onChange={this.onInput}
            onKeyDown={this.stop} /* defect 10: keep card flip from catching typing */
            aria-label={this.t(0, 'Your answer')}
          />
          {this.state.revealed && (
            <div className={classes.revealed} style={this.fontStyle()} aria-live="polite">{card.expectedAnswer}</div>
          )}
        </div>
      );
    }

    const shown = face < faces ? face : 0;
    // defect 3 + FC-012 (defect 1): on reveal, show the AUTHORED correct side AS-IS
    // (its own text/font/normal color/media via renderFace) with NO added caption.
    // The redundant on-card "Correct answer" label was removed per FC-012 — the
    // learner sees only the authored answer, exactly like Fill-in's reveal (which
    // renders card.expectedAnswer with no caption). The GLOBAL Correct Answer
    // control is unchanged. Reveal is reached only via showCorrectAnswer now.
    if (mode === MODE.PICK_ONE && this.state.revealed && (card.correctSideIndex === 0 || card.correctSideIndex === 1)) {
      return this.renderFace(card.items[card.correctSideIndex], card.correctSideIndex);
    }
    return this.renderFace(card.items[shown], shown);
  };

  modeButtons = () => {
    const { mode } = this.state;
    const buttons = [GO_BACK, HELP];
    // defect 4: expose a reachable play control for instruction audio. Instructions
    // listens for the public `playInstructionsAudio` event that the SPEAK button
    // dispatches; add it only when the current card actually has instruction audio.
    const card = this.currentCard();
    if (card && card.instruction && card.instruction.audio) {
      buttons.push(SPEAK);
    }
    if (mode === MODE.FILL_IN) {
      buttons.push({ type: CHECK_ANSWER, onClick: this.checkFillIn });
      // Correct Answer appears only when reveal is enabled AND available (defect 9).
      if (this.showAnswerThreshold() !== null && this.revealAvailable()) {
        buttons.push({ type: CORRECT_ANSWER, onClick: this.showCorrectAnswer });
      }
    }
    // R10 (2026-10-24, OVERRIDES the legacy on-card Check): Pick One now uses the
    // GLOBAL Bar Check Answer. It checks the VISIBLE face atomically. Reveal still
    // obeys the showAnswer threshold via the same Correct Answer control as Fill-in.
    if (mode === MODE.PICK_ONE) {
      buttons.push({ type: CHECK_ANSWER, onClick: this.checkPickOne });
      if (this.showAnswerThreshold() !== null && this.revealAvailable()) {
        buttons.push({ type: CORRECT_ANSWER, onClick: this.showCorrectAnswer });
      }
    }
    if (mode === MODE.MEMORY) {
      // Memory advances through the Global Bar NEXT button, disabled until BOTH
      // faces have been viewed (mastery gate).
      const card = this.currentCard();
      buttons.push({ type: NEXT, onClick: this.memoryNext, disabled: !card || !this.bothFacesViewed(card) });
    }
    return buttons;
  };

  // defect 5: adapt our nested instruction to the Instructions public props.
  // getInstruction uses DIRECT property access (problem[textPath]/[audioPath]),
  // not lodash paths, so we pass a flat problem + an explicit audio base url.
  instructionProblem = (card) => {
    const instr = card.instruction || {};
    return {
      id: card.id,
      instructionText: instr.text || '',
      instructionAudio: instr.audio || '',
    };
  };

  render() {
    const { started, order, done } = this.state;
    if (!started) return null;
    if (!order.length) return <WhiteBox>{this.t(0, 'No cards')}</WhiteBox>;

    const card = this.currentCard();
    const total = order.length;
    const completedCount = Object.keys(this.state.completedCards).length;
    const problemsLeft = done ? 0 : total - completedCount;

    const canFlip = !prefersReducedMotion();
    const flipClass = classnames(classes.card, {
      [classes.flipped]: this.state.face === 1,
      [classes.animate]: canFlip,
    });

    const instr = card.instruction || {};
    const hasInstruction = !!(instr.text || instr.audio);

    // ITEM-3: reserved top for the card region. Use the measured band height when
    // available; otherwise the standing default. Applied as a CSS variable so the
    // module controls the actual padding.
    const reserveVar = {
      '--flc-instr-reserve': `${this.state.instrReservePx || this.INSTR_RESERVE_DEFAULT_PX}px`,
    };

    return (
      <div className={classes.stage} ref={this.stageRef}>
        {/* The shared blocks position THEMSELVES absolutely against the full play
            canvas (.template-wrapper is position:fixed inset:0). This .stage is a
            full-bleed, NON-positioned overlay container (absolute inset:0 anchored
            to .template-wrapper) so it does NOT become their positioning ancestor
            — that keeps Buttons at the real canvas bottom and ProblemsProgress at
            the canvas top-left, instead of anchoring to a short stage. */}
        <ProblemsProgress problemsNumber={total} problemsLeft={problemsLeft} />

        {hasInstruction && (
          /* ITEM-3: template-local wrapper so we can MEASURE the shared band's real
             rendered height (it is position:absolute, but still our DOM descendant).
             The wrapper adds no box of its own (display:contents) so it does not alter
             the band's shared absolute placement. */
          <div ref={this.instrBandRef} className={classes.instrBandProbe}>
            <Instructions
              problem={this.instructionProblem(card)}
              activityAudioBaseUrl={this.assetBaseUrl()}
              textPath="instructionText"
              audioPath="instructionAudio"
              autoPlay={false}
              /* ITEM-3: WIDEN the band toward the shared default so long (3-4 line)
                 instructions wrap to fewer lines before we reserve height. Bounded so
                 it never exceeds the canvas. (Prior 640px cap forced extra wrapping.) */
              wrapperStyle={{ width: 'min(92vmin, 1100px)' }}
            />
          </div>
        )}

        {/* Card centered in the main region. The reserved top (instruction band +
            gap) is MEASURED (ITEM-3) and applied via --flc-instr-reserve; it is the
            same whether or not an instruction is present for a given reserve, so the
            card size is stable, and a taller instruction reserves more (card stays as
            large as the remaining canvas allows). */}
        <div
          className={classnames(classes.content, { [classes.contentNoInstr]: !hasInstruction })}
          style={reserveVar}
        >
          <div className={classes.cardArea}>
            <div className={classes.cardSlot}>
              <div
                className={flipClass}
                role="button"
                tabIndex={0}
                aria-label={`${this.t(0, 'Card face')} ${this.state.face + 1}. ${this.t(0, 'Activate to flip.')}`}
                onClick={this.flip}
                onKeyDown={(e) => {
                  // only the card itself flips on Space/Enter; descendant inputs/
                  // controls keep their keystrokes (typing spaces works).
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.flip();
                  }
                }}
              >
                <div className={classes.faceInner}>
                  <div
                    className={classes.faceContent}
                    style={{ '--flc-frame-url': `url("${this.frameUrl()}")` }}
                  >
                    {/* FC-010: non-visual load probe for the chosen frame design.
                        The frame paints via a CSS ::before background-image (no error
                        event), so this hidden <img> detects a failed design load and
                        steps down to the known fallback frame — never a blank card. */}
                    <img
                      src={this.frameUrl()}
                      alt=""
                      aria-hidden="true"
                      className={classes.frameProbe}
                      onError={this.handleFrameError}
                    />
                    {this.renderCardFace(card)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* R10/R11: feedback is conveyed by the established shared feedback AUDIO
              (playCorrectSound/playIncorrectSound), not custom on-screen text. The
              `feedback` state is retained only as a re-submit guard. */}
        </div>

        {/* ActivityButtons -> Buttons positions itself absolutely at the bottom. */}
        <ActivityButtons buttons={this.modeButtons()} />
      </div>
    );
  }
}

const mapStateToProps = ({ flinkPlay, status }) => ({
  solutionLocale: flinkPlay.solutionLocale,
  // Same source as the project `withTranslate` HOC (status.translate). Routing the
  // template's own labels through this existing public API; unassigned labels use the
  // established translate(0, fallback) convention (English fallback until a content
  // owner assigns a real stringNumber). No new system/records.
  translate: status.translate,
});

export default connect(mapStateToProps, { showStats, playCorrectSound, playIncorrectSound, playWatch })(FlashCards);
